import { TelemetryEvent, TelemetryEventType, TelemetrySeverity, TelemetryDeviceInfo, TelemetryBatchPayload } from './types';
import { getAuthManager } from '../auth/AuthManager';
import { apiFetch, getUltraMeetClientConfig, resolveApiUrl } from '../ultra-meet-config';

const MAX_BUFFER_SIZE = 50;
const FLUSH_INTERVAL_MS = 10_000;
const CLIENT_VERSION = '1.0.0';
const TELEMETRY_ENDPOINT = '/api/v1/telemetry/events';

export class TelemetryClient {
    private buffer: TelemetryEvent[] = [];
    private sessionId: string = '';
    private deviceInfo: TelemetryDeviceInfo = { user_agent: '', platform: '', language: '', screen_resolution: '' };
    private roomName?: string;
    private userIdentity?: string;
    private flushTimer?: ReturnType<typeof setInterval>;
    private initialized = false;

    private boundOnError: ((event: ErrorEvent) => void) | null = null;
    private boundOnUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;
    private boundOnBeforeUnload: (() => void) | null = null;

    init(): void {
        if (this.initialized || typeof window === 'undefined') return;
        this.initialized = true;

        this.sessionId =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : Math.random().toString(36).substring(2) + Date.now().toString(36);

        this.deviceInfo = {
            user_agent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screen_resolution: `${screen.width}x${screen.height}`
        };

        this.boundOnError = (event: ErrorEvent) => {
            this.capture('error.unhandled', 'error', event.message || 'Unknown error', {
                source: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack
            });
        };

        this.boundOnUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            this.capture('error.unhandledrejection', 'error', reason?.message || String(reason) || 'Unhandled promise rejection', {
                stack: reason?.stack
            });
        };

        this.boundOnBeforeUnload = () => {
            this.flushSync();
        };

        window.addEventListener('error', this.boundOnError);
        window.addEventListener('unhandledrejection', this.boundOnUnhandledRejection);
        window.addEventListener('beforeunload', this.boundOnBeforeUnload);

        this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);

        console.log('[Telemetry] Initialized, session:', this.sessionId);
    }

    capture(type: TelemetryEventType, severity: TelemetrySeverity, message: string, metadata?: Record<string, unknown>): void {
        if (!this.initialized) return;

        const event: TelemetryEvent = {
            timestamp: new Date().toISOString(),
            event_type: type,
            severity,
            message,
            user_identity: this.userIdentity,
            room_name: this.roomName,
            metadata,
            device_info: this.deviceInfo,
            session_id: this.sessionId
        };

        this.buffer.push(event);

        if (this.buffer.length >= MAX_BUFFER_SIZE) {
            this.flush();
        }
    }

    captureError(error: Error, metadata?: Record<string, unknown>): void {
        this.capture('error.unhandled', 'error', error.message, {
            ...metadata,
            stack: error.stack
        });
    }

    setRoomContext(roomName: string, identity?: string): void {
        this.roomName = roomName;
        this.userIdentity = identity;
    }

    clearRoomContext(): void {
        this.roomName = undefined;
        this.userIdentity = undefined;
    }

    async flush(): Promise<void> {
        if (this.buffer.length === 0) return;

        let token: string | null = null;
        try {
            const accessTokenProvider = getUltraMeetClientConfig().accessTokenProvider;
            token = accessTokenProvider ? await accessTokenProvider() : await getAuthManager().getAccessToken();
        } catch {
            // No auth available — skip flush
        }
        if (!token) return;

        const events = this.buffer.splice(0);
        const payload: TelemetryBatchPayload = {
            events,
            client_version: CLIENT_VERSION
        };

        try {
            await apiFetch(TELEMETRY_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
        } catch (e) {
            console.warn('[Telemetry] Failed to flush events:', e);
        }
    }

    private flushSync(): void {
        if (this.buffer.length === 0) return;

        const payload: TelemetryBatchPayload = {
            events: this.buffer.splice(0),
            client_version: CLIENT_VERSION
        };

        try {
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
            navigator.sendBeacon(resolveApiUrl(TELEMETRY_ENDPOINT), blob);
        } catch (e) {
            console.warn('[Telemetry] sendBeacon failed:', e);
        }
    }

    destroy(): void {
        if (!this.initialized) return;

        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = undefined;
        }

        if (this.boundOnError) {
            window.removeEventListener('error', this.boundOnError);
        }
        if (this.boundOnUnhandledRejection) {
            window.removeEventListener('unhandledrejection', this.boundOnUnhandledRejection);
        }
        if (this.boundOnBeforeUnload) {
            window.removeEventListener('beforeunload', this.boundOnBeforeUnload);
        }

        this.flush();
        this.initialized = false;
    }
}
